import { Grid, Input, Slider } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import classes from "./TaskmapWizard.module.css";
import { CutoffRange } from "../taskmap.types";

export type TaskmapSliderProps = {
  cutoff: CutoffRange;
  setCutoff: (cutoff: CutoffRange) => void;
  sortedData: number[];
  totalTreeCount: number;
};

function getAffectedTrees(
  sortedData: number[],
  cutoff: CutoffRange,
  totalTreeCount: number,
): number {
  const minIndex = sortedData.findIndex((item) => item >= cutoff.min);
  const maxIndex = sortedData.findLastIndex((item) => item <= cutoff.max);
  return (maxIndex - minIndex + 1) / totalTreeCount;
}

export default function TaskmapSlider({
  cutoff,
  setCutoff,
  sortedData,
  totalTreeCount,
}: TaskmapSliderProps): React.ReactNode {
  const { t, i18n } = useTranslation();

  const formatter = Intl.NumberFormat(i18n.language, {
    style: "percent",
  });

  const minDataValue = Math.floor(sortedData[0]);
  const maxDataValue = Math.ceil(sortedData[sortedData.length - 1]);

  return (
    <div data-testid="taskMapSlider">
      <Grid container spacing={2} alignItems="center">
        <Grid size="auto">
          <Input
            className={classes.numberInput}
            value={cutoff.min}
            size="small"
            onChange={(event) => {
              const newCutoff = {
                min: Math.min(Number(event.target.value), cutoff.max),
                max: Math.max(Number(event.target.value), cutoff.max),
              };
              setCutoff(newCutoff);
            }}
            inputProps={{
              min: minDataValue,
              max: maxDataValue,
              type: "number",
            }}
          />
        </Grid>
        <Grid size="grow">
          <Slider
            value={[cutoff.min, cutoff.max]}
            onChange={(_, newValue) => {
              setCutoff({
                min: (newValue as [number, number])[0],
                max: (newValue as [number, number])[1],
              });
            }}
            valueLabelDisplay="auto"
            getAriaValueText={String}
            min={minDataValue}
            max={maxDataValue}
            step={1}
          />
        </Grid>
        <Grid size="auto">
          <Input
            className={classes.numberInput}
            value={cutoff.max}
            size="small"
            onChange={(event) => {
              const newCutoff = {
                min: Math.min(Number(event.target.value), cutoff.min),
                max: Math.max(Number(event.target.value), cutoff.min),
              };
              setCutoff(newCutoff);
            }}
            inputProps={{
              min: minDataValue,
              max: maxDataValue,
              type: "number",
            }}
          />
        </Grid>
      </Grid>
      <p>
        {t("text.{{percentage}} of trees are selected", {
          percentage: formatter.format(
            getAffectedTrees(sortedData, cutoff, totalTreeCount),
          ),
        })}
      </p>
    </div>
  );
}
